import { TrackedObject, TrackedRecord } from "./collab";
import { Node, Schema } from "./nodes"

/**
 * Rercursively match all TrackedObjects to their types in the schema, and each time a match is found, mark it as visited and check for any migrations to run on it.
 * After recursing through everything, do it again for each object that was migrated, and repeat until no more migrations are found.
 * This shouldn't be overly expensive, since the size of the migrated object should be significantly smaller than the original root object passed in initially.
 */
export function doMigrations(node: Node, schema: Schema, obj: TrackedObject, migrations: Record<string, (qType: TrackedRecord) => void>): void {
  const migratedObjects: TrackedObject[] = [];
  matchObjectTypes(node, schema, obj, migrations, migratedObjects, new Set<string>());
  // Keep checking until no more migrations are found
  if (migratedObjects.length > 0) {
    console.log('Migrated objects: ', migratedObjects);
    doMigrations(node, schema, obj, migrations);
  }
}

// This is a Type Guard using a Type Predicate
// https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
function isTrackedRecord(obj: TrackedObject): obj is TrackedRecord {
  return (obj as TrackedRecord).set !== undefined;
}

function markVisitedAndCheckMigration(node: Node, obj: TrackedObject, migrations: Record<string, (qType: TrackedRecord) => void>, migratedObjects: TrackedObject[], visited: Set<string>) {
  visited.add(obj.id);
  if (node.meta?.migration) {
    if (isTrackedRecord(obj)) {
      migrations[node.meta.migration as keyof typeof migrations](obj);
      migratedObjects.push(obj);
    } else {
      // If we ever need to, we can accommodate migrating TrackedArrays,
      // but we haven't needed to yet, and this keeps Migrations.ts a bit cleaner
      console.log('Error: Tried to migrate TrackedArray');
    }
  }
}

function matchObjectTypes(node: Node, schema: Schema, obj: TrackedObject, migrations: Record<string, (qType: TrackedRecord) => void>, migratedObjects: TrackedObject[], visited: Set<string>): boolean {
  if (obj === undefined) {
    return false;
  }
  switch (node.kind) {
    case 'object': {
      // Check that all required properties are present
      if (!node.required.reduce((acc, p) => acc && obj.value[p as keyof typeof obj.value] !== undefined, true)) {
        return false;
      }
      // Check that all known properties are typed properly.
      for (const p of Object.keys(obj.value as Record<string, TrackedObject>)) {
        if (node.props[p]) {
          let found = matchObjectTypes(node.props[p], schema, obj.value[p], migrations, migratedObjects, visited);
          if (!found) return false;
          if (!visited.has(obj.value[p].id)) {
            markVisitedAndCheckMigration(node.props[p], obj.value[p], migrations, migratedObjects, visited);
          }
        }
      }
      return true;
    }
    case 'array': {
      if (Array.isArray(obj.value)) {
        for (const v of obj.value) {
          let found = matchObjectTypes(node.element, schema, v, migrations, migratedObjects, visited);
          // We need to keep looking through the array, even if we do not find a match for a given element.
          if (found && !visited.has(v.id)) {
            markVisitedAndCheckMigration(node.element, v, migrations, migratedObjects, visited);
          }
        }
        return true;
      }
      return false;
    }
    case 'ref': {
      let found = matchObjectTypes(schema[node.label], schema, obj, migrations, migratedObjects, visited);
      // We don't check visited here because the refs aren't unique like the other Node kinds
      if (found) {
        markVisitedAndCheckMigration(schema[node.label], obj, migrations, migratedObjects, visited);
      }
      return found;
    }
    case 'string': {
      return typeof obj.value === 'string';
    }
    case 'number': {
      return typeof obj.value === 'number';
    }
    case 'boolean': {
      return typeof obj.value === 'boolean';
    }
    case 'literal': {
      return obj.value === node.value;
    }
    case 'union': {
      for (const subNode of node.options) {
        let found = matchObjectTypes(subNode, schema, obj, migrations, migratedObjects, visited);
        if (found && !visited.has(obj.id)) {
          markVisitedAndCheckMigration(subNode, obj, migrations, migratedObjects, visited);
        }
        // If we find a matching branch, don't bother checking the rest.
        if (found) return true;
      }
      return false;
    }
    case 'intersection': {
      for (const subNode of node.constraints) {
        let found = matchObjectTypes(subNode, schema, obj, migrations, migratedObjects, visited);
        // If any branch doesn't match, don't bother checking the rest.
        if (!found) return false;
        if (found && !visited.has(obj.id)) {
          markVisitedAndCheckMigration(subNode, obj, migrations, migratedObjects, visited);
        }
      }
      return true;
    }
    default: {
      return false;
    }
  }
}
